import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

interface AdminContextType {
  isAdmin: boolean;
  loading: boolean;
  error: string | null;
}

const AdminContext = createContext<AdminContextType>({
  isAdmin: false,
  loading: true,
  error: null,
});

export const useAdmin = () => useContext(AdminContext);

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkAdminStatus = async () => {
      // Wait for auth to finish loading before making any decisions
      if (authLoading) {
        return;
      }

      if (!user) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await api.get('/auth/admin-status');
        setIsAdmin(response.data.isAdmin);
        setError(null);
      } catch (err) {
        console.error('Error checking admin status:', err);
        setIsAdmin(false);
        setError('Failed to check admin status');
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [user, authLoading]);

  return (
    <AdminContext.Provider value={{ isAdmin, loading, error }}>
      {children}
    </AdminContext.Provider>
  );
};
